import { Badge, Box, Button, Flex, Spinner, Stack, Text } from "@chakra-ui/react";
import { Card } from "../../ui/Card";
import { ApiAuth } from "../../../lib/api";
import { usePlans } from "./hooks";
import type { PatientPlan } from "./types";
import { itemWords, renewalWords, stateWords, validityWords, whereWords } from "./planWords";

// The patient's memberships and packages — what is left on each, until when,
// where it can be used and when a membership renews. Read-only: buying a plan
// here is not offered yet (decision D17).

const STATE_TONE: Record<string, string> = {
  active: "green",
  not_started: "blue",
  used_up: "gray",
  expired: "gray",
  ended: "gray",
  cancelled: "gray",
};

function PlanCard({ plan }: { plan: PatientPlan }) {
  const renewal = renewalWords(plan);
  const where = whereWords(plan);
  return (
    <Card p={4}>
      <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
        <Box minW={0}>
          <Text fontSize="15px" fontWeight="600" color="#1a1f36">
            {plan.name}
          </Text>
          <Text fontSize="13px" color="#6b7280">
            {plan.kind === "membership" ? "Membership" : "Package"} · {validityWords(plan)}
          </Text>
        </Box>
        <Badge colorScheme={STATE_TONE[plan.state] || "gray"} flexShrink={0}>
          {stateWords(plan)}
        </Badge>
      </Flex>
      <Stack spacing={1} mt={3}>
        {plan.items.map((item, index) => (
          <Flex key={`${item.name}-${index}`} justify="space-between" gap={3} fontSize="14px">
            <Text color="#1a1f36" minW={0}>
              {item.name}
            </Text>
            <Text color="#374151" textAlign="right" flexShrink={0}>
              {itemWords(item)}
            </Text>
          </Flex>
        ))}
      </Stack>
      {where || renewal || plan.note ? (
        <Stack spacing={0.5} mt={3} fontSize="13px" color="#6b7280">
          {where ? <Text>{where}</Text> : null}
          {renewal ? <Text>{renewal}</Text> : null}
          {plan.note ? <Text color="#92400e">{plan.note}</Text> : null}
        </Stack>
      ) : null}
    </Card>
  );
}

/** A list of plan cards — the patient's own, or a dependent's. */
export const PlanList = ({ plans }: { plans: PatientPlan[] }) => (
  <Stack spacing={3}>
    {plans.map((plan) => (
      <PlanCard key={plan.planId} plan={plan} />
    ))}
  </Stack>
);

export const PlansView = ({ auth }: { auth: ApiAuth }) => {
  const query = usePlans(auth);
  if (query.isLoading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner />
      </Flex>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Card p={6} textAlign="center">
        <Text color="#1a1f36" fontWeight="500">
          We could not load your plans.
        </Text>
        <Button mt={3} size="sm" onClick={() => query.refetch()}>
          Try again
        </Button>
      </Card>
    );
  }
  if (query.data.plans.length === 0) {
    return (
      <Card p={6} textAlign="center">
        <Text color="#6b7280">You have no memberships or packages.</Text>
      </Card>
    );
  }
  return <PlanList plans={query.data.plans} />;
};
